import React from "react";
import { Link } from "react-router-dom";
import { Quote, Star } from "lucide-react";
import { FEATURED, WHY_IVI } from "../../mock";
import { Icon, Reveal, Eyebrow, SectionHeading } from "../common";

export default function Featured() {
  return (
    <>
      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
          <div className="grid items-stretch gap-8 lg:grid-cols-2">
            <Reveal>
              <div className="relative h-full overflow-hidden rounded-[28px] bg-ivi-slate p-9 text-white lg:p-11">
                <span className="absolute right-0 top-8 rounded-l-full bg-ivi-lime px-5 py-1.5 font-heading text-[12px] font-extrabold uppercase tracking-[0.18em] text-ivi-slate">
                  {FEATURED.ribbon}
                </span>
                <p className="font-heading text-[13px] font-bold uppercase tracking-[0.18em] text-ivi-lime">
                  {FEATURED.eyebrow}
                </p>
                <h3 className="mt-3 font-heading text-[32px] font-extrabold leading-tight sm:text-[38px]">
                  {FEATURED.title}
                </h3>
                <div className="mt-6 flex items-end gap-2">
                  <span className="font-heading text-[54px] font-extrabold leading-none text-white">
                    ${FEATURED.price}
                  </span>
                  <span className="pb-2 text-[14px] text-white/65">
                    {FEATURED.currency} {FEATURED.note}
                  </span>
                </div>
                <p className="mt-6 text-[16px] leading-relaxed text-white/80">{FEATURED.body}</p>

                <p className="mt-8 font-heading text-[13px] font-bold uppercase tracking-[0.16em] text-white/50">
                  Includes
                </p>
                <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                  {FEATURED.includes.map((inc) => (
                    <li key={inc.label} className="flex items-center gap-3 text-[15px] text-white/90">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white/10 text-ivi-lime">
                        <Icon name={inc.icon} className="h-4 w-4" />
                      </span>
                      {inc.label}
                    </li>
                  ))}
                </ul>

                <Link
                  to="/projects/volunteer-in-bali"
                  className="mt-9 inline-flex items-center justify-center rounded-full bg-ivi-green px-7 py-3.5 font-heading text-[15px] font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-ivi-greenDark"
                >
                  Project details
                </Link>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="flex h-full flex-col gap-6">
                <div className="overflow-hidden rounded-[28px] shadow-[0_24px_60px_-34px_rgba(24,40,32,0.55)]">
                  <img
                    src={FEATURED.img}
                    alt="Lombok turtle conservation volunteers"
                    className="h-[320px] w-full object-cover transition-transform duration-[1200ms] hover:scale-105"
                  />
                </div>
                <div className="relative flex-1 rounded-[28px] bg-ivi-mint p-9">
                  <Quote className="absolute right-7 top-7 h-12 w-12 text-white" />
                  <div className="flex gap-1">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star key={i} className="h-4 w-4 fill-ivi-green text-ivi-green" />
                    ))}
                  </div>
                  <p className="relative mt-5 text-[16px] leading-relaxed text-ivi-slate">
                    &ldquo;{FEATURED.quote}&rdquo;
                  </p>
                  <p className="mt-5 font-heading text-[15px] font-bold text-ivi-greenDark">
                    {FEATURED.author}, Turtle Conservation volunteer
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ivi-mint py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-[0.85fr_1.15fr]">
            <div>
              <Eyebrow>Why IVI?</Eyebrow>
              <h2 className="mt-5 font-heading text-[34px] font-extrabold leading-[1.12] tracking-[-0.02em] text-ivi-slate sm:text-[44px]">
                Thirty-five years of doing this properly
              </h2>
              <p className="mt-5 text-[17px] leading-relaxed text-ivi-slateSoft">
                We audit our projects, we pay local staff, we publish our fees and we answer the
                phone at 3am if you need us.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {WHY_IVI.map((w, i) => (
                <Reveal key={w.title} delay={i * 80}>
                  <div className="group flex h-full gap-4 rounded-3xl bg-white p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_24px_46px_-28px_rgba(24,40,32,0.45)]">
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-ivi-mint text-ivi-greenDark transition-colors duration-300 group-hover:bg-ivi-green group-hover:text-white">
                      <Icon name={w.icon} className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-heading text-[17px] font-bold text-ivi-slate">{w.title}</h3>
                      <p className="mt-2 text-[14px] leading-relaxed text-ivi-slateSoft">{w.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
